import React, { useState, useEffect, useCallback } from "react";
import { BookingRecord, PaginatedResponse, PaginationMeta } from "../../types";
import {
  getOrders,
  getOrdersStats,
  OrdersStats,
} from "../../services/api/admin/orders";
import { subscribeToOrders, OrderEventData } from "../../services/api/realtime";
import {
  Search,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  MessageCircle,
  Phone,
  Mail,
  User,
  Filter,
  Package,
  CreditCard,
  CheckSquare,
  Ban,
} from "lucide-react";
import Pagination from "../Pagination";
import Swal from "sweetalert2";

interface OrdersManagerProps {
  onSelectBooking: (booking: BookingRecord) => void;
}

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    PAID: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PENDING: "bg-amber-100 text-amber-700 border-amber-200",
    CANCELLED: "bg-rose-100 text-rose-700 border-rose-200",
    COMPLETED: "bg-blue-100 text-blue-700 border-blue-200",
  };
  return (
    <span
      className={`inline - flex items - center px - 2 py - 1 rounded - full text - [10px] font - black uppercase border ${
        styles[status] || "bg-gray-100"
      } `}
    >
      {status}
    </span>
  );
};

export const OrdersManager: React.FC<OrdersManagerProps> = ({
  onSelectBooking,
}) => {
  const [data, setData] = useState<PaginatedResponse<BookingRecord> | null>(
    null
  );
  const [stats, setStats] = useState<OrdersStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const result = await getOrdersStats();
      setStats(result);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getOrders({
        page,
        limit,
        search: searchQuery || undefined,
        status: statusFilter !== "ALL" ? statusFilter.toLowerCase() : undefined,
        startDate: dateFilter || undefined,
        endDate: dateFilter || undefined,
      });
      setData(result);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Gagal memuat data pesanan",
        text: "Terjadi kesalahan saat memuat data pesanan. Silakan coba lagi.",
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, statusFilter, dateFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  useEffect(() => {
    const unsubscribe = subscribeToOrders(
      (data: OrderEventData) => {
        fetchData();

        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: "success",
          title: "Order Baru",
          text: `${data.bookingCode} - ${data.customerName}`,
        });
      },
      () => {
        fetchData();
      }
    );

    return () => {
      unsubscribe();
    };
  }, [fetchData, fetchStats]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const paginationMeta: PaginationMeta = data?.meta || {
    total: 0,
    perPage: limit,
    currentPage: page,
    lastPage: 1,
    firstPage: 1,
  };

  const handleWhatsApp = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    let p = phone.replace(/\D/g, "");
    if (p.startsWith("0")) p = "62" + p.substring(1);
    window.open(`https://wa.me/${p}`, "_blank");
  };

  return (
    <div className="space-y-6 animate-fadeIn h-full flex flex-col pb-10">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-indigo-100 p-2 rounded-xl">
                <Package className="w-4 h-4 text-indigo-600" />
              </div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Total
              </span>
            </div>
            <p className="text-2xl font-black text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-amber-100 p-2 rounded-xl">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Pending
              </span>
            </div>
            <p className="text-2xl font-black text-amber-600">
              {stats.pending}
            </p>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-emerald-100 p-2 rounded-xl">
                <CreditCard className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Paid
              </span>
            </div>
            <p className="text-2xl font-black text-emerald-600">{stats.paid}</p>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-100 p-2 rounded-xl">
                <CheckSquare className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Selesai
              </span>
            </div>
            <p className="text-2xl font-black text-blue-600">
              {stats.completed}
            </p>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-rose-100 p-2 rounded-xl">
                <Ban className="w-4 h-4 text-rose-600" />
              </div>
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Batal
              </span>
            </div>
            <p className="text-2xl font-black text-rose-600">
              {stats.cancelled}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col lg:flex-row gap-4 justify-between items-center">
        <div className="relative w-full lg:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari Kode, Nama, WhatsApp..."
            className="w-full pl-10 pr-4 py-3 bg-white text-gray-900 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-giz-primary"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="date"
            className="pl-3 pr-3 py-3 bg-white text-gray-900 border border-gray-200 rounded-xl text-xs font-black outline-none"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setPage(1);
            }}
          />
          <select
            className="pl-4 pr-10 py-3 bg-white text-gray-900 border border-gray-200 rounded-xl text-xs font-black uppercase outline-none"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="ALL">SEMUA STATUS</option>
            <option value="PENDING">PENDING</option>
            <option value="PAID">LUNAS (PAID)</option>
            <option value="COMPLETED">SELESAI</option>
            <option value="CANCELLED">BATAL (CANCEL)</option>
          </select>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-[2rem] shadow-sm flex-1 overflow-hidden flex flex-col">
        <div className="overflow-auto flex-1 scrollbar-thin">
          <table className="w-full text-sm text-left border-separate border-spacing-0">
            <thead className="bg-slate-50 text-slate-500 font-black uppercase text-[10px] tracking-widest sticky top-0 z-20">
              <tr>
                <th className="px-6 py-5 rounded-tl-[2rem]">ID Booking</th>
                <th className="px-6 py-5">Nama Pengunjung</th>
                <th className="px-6 py-5">Kontak</th>
                <th className="px-6 py-5 text-center">Kedatangan</th>
                <th className="px-6 py-5 text-center">Status</th>
                <th className="px-6 py-5 text-center rounded-tr-[2rem]">
                  Detail
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-6 py-8">
                      <div className="h-10 bg-gray-50 rounded-2xl animate-pulse"></div>
                    </td>
                  </tr>
                ))
              ) : data && data.data.length > 0 ? (
                data.data.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer group"
                    onClick={() => onSelectBooking(item)}
                  >
                    <td className="px-6 py-4">
                      <div className="font-mono font-black text-gray-900">
                        {item.code}
                      </div>
                      <div className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">
                        {item.ticketType}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-black text-gray-800 text-xs uppercase">
                        {item.customerName}
                      </div>
                      <div className="text-[10px] text-indigo-500 font-bold mt-0.5">
                        {item.pax} Pax
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={(e) => handleWhatsApp(e, item.phone)}
                          className="text-[10px] font-bold text-emerald-600 flex items-center gap-1 hover:underline"
                        >
                          <MessageCircle className="w-3 h-3" /> {item.phone}
                        </button>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {item.email}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-xs font-bold text-gray-700">
                        {item.date}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button className="p-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-32 text-center text-gray-300 font-black uppercase"
                  >
                    Tidak ada riwayat pesanan
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 rounded-b-[2rem]">
          <Pagination
            meta={{ ...paginationMeta, currentPage: page }}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersManager;
